import { useCallback, useEffect, useRef } from 'react'
import { connectWithTicket, WsCloseCode, WsHandshakeError } from '@shared/api/wsTicket'

/** Пауза перед повтором. Растёт до минуты, чтобы упавший бэк не собирал с
 *  каждой открытой вкладки по запросу в секунду. */
const RECONNECT_DELAYS_MS = [1_000, 3_000, 8_000, 20_000, 60_000]

function delayFor(attempt: number): number {
  return RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]
}

export interface TicketedSocketConfig {
  /** Путь сокета после базы, например `/chat/group/`. */
  path: string
  /** Пока false — не подключаемся и рвём открытое. */
  enabled: boolean
  /** Язык для бэка; смена значения переподключает сокет. */
  lang?: string | null
  /** Разбор входящего кадра. Дедуп по `event_id` уже сделан. */
  onFrame: (data: unknown) => void
  /** Тот же кадр ДО дедупа по `event_id`. Нужен тому, кто опознаёт кадр по
   *  своему ключу (эхо с `client_message_id`): повтор отправки с тем же UUID
   *  сервер может вернуть с прежним `event_id`, и после дедупа ожидающий
   *  упёрся бы в таймаут. Повторы здесь придут тоже — обработчик идемпотентен. */
  onRawFrame?: (data: unknown) => void
  /** Бэк закрыл доступ (4403 при закрытии или на рукопожатии): переподключения
   *  не будет, экран должен сказать об этом словами. */
  onForbidden?: () => void
}

export interface TicketedSocketApi {
  /** Отправляет кадр. `false` — сокет не открыт, вызывающий решает, что делать. */
  send: (payload: Record<string, unknown>) => boolean
  /** Переподключиться немедленно: закрывает текущий сокет и открывает новый с
   *  новым тикетом. Нужен там, где полный снапшот приходит только на
   *  подключении. */
  reconnect: () => void
}

interface FrameWithEventId {
  event_id?: string
}

/**
 * Сокет на одноразовых тикетах: выпуск, подключение, переподключение и дедуп.
 *
 * Три правила, которые пришли из контракта и живой проверки:
 *
 * 1. **Тикет одноразовый и вытесняемый.** Каждый коннект начинается с нового
 *    тикета, а сама выдача сериализована в `connectWithTicket` — параллельный
 *    выпуск обнуляет чужие тикеты.
 * 2. **4403 — это не «попробуй ещё раз».** Роль или владение не изменятся от
 *    повтора, поэтому такой сокет больше не переподключается. 4401, наоборот,
 *    лечится новым тикетом (истёк access-токен или тикет вытеснили).
 * 3. **Доставка at-least-once.** Один и тот же `event_id` может прийти дважды
 *    после реконнекта — повторы отбрасываются здесь, чтобы каждый потребитель
 *    не изобретал это заново.
 */
export function useTicketedSocket({
  path,
  enabled,
  lang,
  onFrame,
  onRawFrame,
  onForbidden,
}: TicketedSocketConfig): TicketedSocketApi {
  const wsRef = useRef<WebSocket | null>(null)
  const seenEventIds = useRef<Set<string>>(new Set())
  const reopenRef = useRef<() => void>(() => {})

  // Обработчик читаем через ref: он пересоздаётся на каждый рендер, а
  // переподключаться из-за этого сокет не должен.
  const onFrameRef = useRef(onFrame)
  const onRawFrameRef = useRef(onRawFrame)
  const onForbiddenRef = useRef(onForbidden)
  useEffect(() => {
    onFrameRef.current = onFrame
    onRawFrameRef.current = onRawFrame
    onForbiddenRef.current = onForbidden
  })

  const send = useCallback((payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return false
    wsRef.current.send(JSON.stringify(payload))
    return true
  }, [])

  const reconnect = useCallback(() => reopenRef.current(), [])

  useEffect(() => {
    if (!enabled) return

    let disposed = false
    let attempt = 0
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    const scheduleRetry = () => {
      if (disposed) return
      retryTimer = setTimeout(open, delayFor(attempt))
      attempt += 1
    }

    async function open() {
      if (disposed) return
      try {
        const ws = await connectWithTicket({ path, lang })
        if (disposed) {
          ws.close(WsCloseCode.normal)
          return
        }
        wsRef.current = ws
        attempt = 0

        ws.onmessage = (event: MessageEvent) => {
          let data: unknown
          try {
            data = JSON.parse(event.data as string)
          } catch {
            // Битый кадр — не повод рвать соединение: следующий может быть
            // валидным, а история уже получена.
            console.warn('[ws] непарсящийся кадр', path)
            return
          }
          onRawFrameRef.current?.(data)
          const eventId = (data as FrameWithEventId)?.event_id
          if (eventId) {
            if (seenEventIds.current.has(eventId)) return
            seenEventIds.current.add(eventId)
          }
          onFrameRef.current(data)
        }

        ws.onclose = (event) => {
          wsRef.current = null
          if (disposed || event.code === WsCloseCode.normal) return
          if (event.code === WsCloseCode.forbidden) {
            console.warn('[ws] доступ запрещён, переподключение отменено', path)
            onForbiddenRef.current?.()
            return
          }
          scheduleRetry()
        }

        ws.onerror = () => ws.close()
      } catch (error) {
        if (disposed) return
        if (error instanceof WsHandshakeError && error.code === WsCloseCode.forbidden) {
          console.warn('[ws] доступ запрещён на хендшейке', path)
          onForbiddenRef.current?.()
          return
        }
        scheduleRetry()
      }
    }

    reopenRef.current = () => {
      if (disposed) return
      if (retryTimer) clearTimeout(retryTimer)
      attempt = 0
      // Закрываем как «нормально», чтобы `onclose` не запустил ещё и обычный
      // ретрай поверх этого переподключения.
      wsRef.current?.close(WsCloseCode.normal)
      wsRef.current = null
      void open()
    }

    void open()

    return () => {
      disposed = true
      reopenRef.current = () => {}
      if (retryTimer) clearTimeout(retryTimer)
      wsRef.current?.close(WsCloseCode.normal)
      wsRef.current = null
    }
  }, [path, enabled, lang])

  return { send, reconnect }
}
